import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Save, ArrowLeft, X, Tag, Calendar, Package, Store, Folder, Plus, Trash2, Hash, Upload, Loader2, X as XIcon } from 'lucide-react'
import { toast } from 'react-hot-toast'
import { getImageUrl } from '../../config/imageConfig'
import useOfferStore from '@/stores/offerStore'
import useAppConfigStore from '@/stores/appConfigStore'
import { CreateOfferDto, OfferImage } from '@/services/offerService'
import { merchantService } from '@/services/merchantService'
import { categoryService } from '@/services/categoryService'
import { productService } from '@/services/productService'
import { DetailSection } from '../ui/DetailSection'

const OfferForm = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const {
    currentOffer,
    loading,
    error,
    fetchOfferById,
    createOffer,
    updateOffer,
    clearError,
    clearCurrentOffer
  } = useOfferStore()
  
  const { systemConfig, availableCurrencies } = useAppConfigStore()

  const [merchants, setMerchants] = useState<any[]>([])
  const [categories, setCategories] = useState<any[]>([])
  const [products, setProducts] = useState<any[]>([])
  const [selectedProducts, setSelectedProducts] = useState<Array<{ product_id: string; quantity: number }>>([])
  const [formData, setFormData] = useState<CreateOfferDto>({
    merchant_id: '',
    type: 'discount',
    title: '',
    description: '',
    original_price: null,
    discount_price: null,
    discount_percentage: null,
    category_id: null,
    start_date: null,
    end_date: null,
    max_quantity: null,
    currency: null,
    preparation_time_minutes: null,
    is_surprise_bag: false,
    surprise_bag_price: null,
    surprise_bag_label: '',
    possible_contents: [],
    products: []
  })

  // Separate state for string values during typing
  const [priceInputs, setPriceInputs] = useState({
    original_price: '',
    discount_price: '',
    discount_percentage: '',
    surprise_bag_price: ''
  })
  
  const [formError, setFormError] = useState<string | null>(null)

  // Image upload states
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([])
  const [imagePreviews, setImagePreviews] = useState<string[]>([])
  const [existingImages, setExistingImages] = useState<OfferImage[]>([]) // Existing images from database
  const [imagesToDelete, setImagesToDelete] = useState<string[]>([]) // Image IDs to delete

  useEffect(() => {
    const loadMerchants = async () => {
      try {
        const response = await merchantService.getAll({})
        setMerchants(response.merchants)
      } catch (error) {
        console.error('Failed to load merchants:', error)
      }
    }
    const loadCategories = async () => {
      try {
        const response = await categoryService.getAll({})
        setCategories(response.data)
      } catch (error) {
        console.error('Failed to load categories:', error)
      }
    }
    const loadProducts = async () => {
      try {
        const response = await productService.getAll({})
        setProducts(response.data || [])
      } catch (error) {
        console.error('Failed to load products:', error)
      }
    }
    loadMerchants()
    loadCategories()
    loadProducts()
  }, [])

  useEffect(() => {
    if (id) {
      fetchOfferById(id)
    } else {
      clearCurrentOffer()
    }
  }, [id])

  useEffect(() => {
    if (currentOffer && id) {
      const productsList = currentOffer.products?.map(p => ({
        product_id: p.product_id,
        quantity: p.quantity
      })) || []
      
      // Convert ISO dates to datetime-local format (YYYY-MM-DDTHH:mm)
      const formatDateForInput = (dateString: string | null) => {
        if (!dateString) return null
        const date = new Date(dateString)
        // Convert to local timezone and format as YYYY-MM-DDTHH:mm
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        return `${year}-${month}-${day}T${hours}:${minutes}`
      }
      
      setFormData({
        merchant_id: currentOffer.merchant_id,
        type: currentOffer.type,
        title: currentOffer.title,
        description: currentOffer.description,
        original_price: currentOffer.original_price,
        discount_price: currentOffer.discount_price,
        discount_percentage: currentOffer.discount_percentage,
        category_id: currentOffer.category_id,
        start_date: formatDateForInput(currentOffer.start_date),
        end_date: formatDateForInput(currentOffer.end_date),
        max_quantity: currentOffer.max_quantity,
        products: productsList
      })
      
      // Initialize price inputs with string values
      setPriceInputs({
        original_price: currentOffer.original_price?.toString() || '',
        discount_price: currentOffer.discount_price?.toString() || '',
        discount_percentage: currentOffer.discount_percentage?.toString() || ''
      })
      
      setSelectedProducts(productsList)
      
      // Handle existing images
      if (currentOffer.images) {
        setExistingImages(currentOffer.images)
      } else {
        setExistingImages([])
      }
      setImagesToDelete([])
      setUploadedFiles([])
      setImagePreviews([])
    } else if (!id) {
      // Reset for new offer
      setExistingImages([])
      setImagesToDelete([])
      setUploadedFiles([])
      setImagePreviews([])
    }
  }, [currentOffer, id])

  // Handle file upload
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newFiles = Array.from(files);
    setUploadedFiles(prev => [...prev, ...newFiles]);

    // Create previews
    newFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews(prev => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });

    // Clear input
    e.target.value = '';
  };

  const removeUploadedFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = (imageId: string) => {
    setExistingImages(prev => prev.filter(img => img.image_id !== imageId));
    setImagesToDelete(prev => [...prev, imageId]);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target
    
    // Handle checkbox toggle for is_surprise_bag
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }))
      return
    }
    
    // For price fields, update the string input state
    if (name === 'original_price' || name === 'discount_price' || name === 'discount_percentage' || name === 'surprise_bag_price') {
      // Validate numeric input with optional decimal point and optional minus sign
      const numericRegex = /^-?\d*\.?\d*$/
      if (value === '' || numericRegex.test(value)) {
        setPriceInputs(prev => ({
          ...prev,
          [name]: value
        }))
      }
      return
    }
    
    // For other fields, update formData directly
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handlePriceBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name } = e.target
    const stringValue = priceInputs[name as keyof typeof priceInputs]
    
    // Parse the string value to number (or null if empty)
    let parsedValue: number | null = null
    if (stringValue.trim() !== '') {
      const num = parseFloat(stringValue)
      if (!isNaN(num) && num > 0) {
        parsedValue = num
      }
    }
    
    // Create updated form data with the parsed value
    const updatedFormData = {
      ...formData,
      [name]: parsedValue
    }
    
    // Get current values after update
    const original_price = updatedFormData.original_price
    const discount_price = updatedFormData.discount_price
    const discount_percentage = updatedFormData.discount_percentage
    
    // Helper to check if a value is valid (not null/undefined and > 0)
    const isValid = (val: any): boolean => {
      return val !== null && val !== undefined && val > 0
    }
    
    // Check which fields are valid
    const hasOriginal = isValid(original_price)
    const hasDiscount = isValid(discount_price)
    const hasPercentage = isValid(discount_percentage) && (discount_percentage ?? 0) < 100
    
    // Count valid fields
    const validCount = [hasOriginal, hasDiscount, hasPercentage].filter(Boolean).length
    
    let finalFormData = { ...updatedFormData }
    let updatedPriceInputs = { ...priceInputs }
    
    // Check if the blurred field is being emptied
    const isFieldBeingEmptied = stringValue.trim() === ''
    
    // Determine which calculation to perform based on which fields are valid
    // We want to calculate when we have exactly 2 valid fields
    // OR when we have 3 valid fields but one was just changed (not emptied)
    
    if (validCount === 2) {
      // We have exactly 2 valid fields - calculate the third
      // Case 1: original_price and discount_percentage provided → calculate discount_price
      if (hasOriginal && hasPercentage && original_price && discount_percentage) {
        const calculatedDiscountPrice = original_price * (1 - discount_percentage / 100)
        finalFormData.discount_price = parseFloat(calculatedDiscountPrice.toFixed(2))
        updatedPriceInputs.discount_price = calculatedDiscountPrice.toFixed(2)
      }
      
      // Case 2: original_price and discount_price provided → calculate discount_percentage
      else if (hasOriginal && hasDiscount && original_price && discount_price) {
        const calculatedDiscountPercentage = ((original_price - discount_price) / original_price) * 100
        finalFormData.discount_percentage = parseFloat(calculatedDiscountPercentage.toFixed(2))
        updatedPriceInputs.discount_percentage = calculatedDiscountPercentage.toFixed(2)
      }
      
      // Case 3: discount_price and discount_percentage provided → calculate original_price
      else if (hasDiscount && hasPercentage && discount_price && discount_percentage) {
        const calculatedOriginalPrice = discount_price / (1 - discount_percentage / 100)
        finalFormData.original_price = parseFloat(calculatedOriginalPrice.toFixed(2))
        updatedPriceInputs.original_price = calculatedOriginalPrice.toFixed(2)
      }
    } else if (validCount === 3 && !isFieldBeingEmptied) {
      // We have 3 valid fields and user changed one (not emptied)
      // Recalculate based on the changed field and one other field
      // This is more complex but provides better UX
      
      if (name === 'original_price' && original_price && discount_price && discount_percentage) {
        // User changed original price - recalculate percentage based on new original and discount
        const calculatedDiscountPercentage = ((original_price - discount_price) / original_price) * 100
        finalFormData.discount_percentage = parseFloat(calculatedDiscountPercentage.toFixed(2))
        updatedPriceInputs.discount_percentage = calculatedDiscountPercentage.toFixed(2)
      } else if (name === 'discount_price' && original_price && discount_price && discount_percentage) {
        // User changed discount price - recalculate percentage based on original and new discount
        const calculatedDiscountPercentage = ((original_price - discount_price) / original_price) * 100
        finalFormData.discount_percentage = parseFloat(calculatedDiscountPercentage.toFixed(2))
        updatedPriceInputs.discount_percentage = calculatedDiscountPercentage.toFixed(2)
      } else if (name === 'discount_percentage' && original_price && discount_price && discount_percentage) {
        // User changed percentage - recalculate discount price based on original and new percentage
        const calculatedDiscountPrice = original_price * (1 - discount_percentage / 100)
        finalFormData.discount_price = parseFloat(calculatedDiscountPrice.toFixed(2))
        updatedPriceInputs.discount_price = calculatedDiscountPrice.toFixed(2)
      }
    } else if (validCount < 2 && isFieldBeingEmptied) {
      // If we have less than 2 valid fields AND a field is being emptied
      // Clear dependent calculated fields to prevent infinite loops
      if (name === 'original_price') {
        // If original price is emptied, clear calculated fields
        finalFormData.discount_price = null
        finalFormData.discount_percentage = null
        updatedPriceInputs.discount_price = ''
        updatedPriceInputs.discount_percentage = ''
      } else if (name === 'discount_price') {
        // If discount price is emptied, clear percentage (but keep original)
        finalFormData.discount_percentage = null
        updatedPriceInputs.discount_percentage = ''
      } else if (name === 'discount_percentage') {
        // If discount percentage is emptied, clear discount price (but keep original)
        finalFormData.discount_price = null
        updatedPriceInputs.discount_price = ''
      }
    }
    
    // Update form data and price inputs
    setFormData(finalFormData)
    setPriceInputs(updatedPriceInputs)
  }

  const handleAddProduct = () => {
    if (selectedProducts.length >= 10) return
    setSelectedProducts([...selectedProducts, { product_id: '', quantity: 1 }])
  }

  const handleRemoveProduct = (index: number) => {
    setSelectedProducts(selectedProducts.filter((_, i) => i !== index))
  }

  const handleProductChange = (index: number, field: 'product_id' | 'quantity', value: string) => {
    const updated = [...selectedProducts]
    if (field === 'product_id') {
      updated[index].product_id = value
    } else {
      updated[index].quantity = parseInt(value) || 1
    }
    setSelectedProducts(updated)
  }

  // Update formData products when selectedProducts changes
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      products: selectedProducts.filter(p => p.product_id.trim() !== '')
    }))
  }, [selectedProducts])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    clearError()
    setFormError(null)

    // Validation des champs requis
    if (!formData.title?.trim()) {
      toast.error('Le titre est requis')
      return
    }
    
    if (!formData.type) {
      toast.error('Le type d\'offre est requis')
      return
    }
    
    if (!formData.start_date?.trim()) {
      toast.error('La date de début est requise')
      return
    }

    if (!formData.merchant_id) {
      toast.error('Le marchand est requis')
      return
    }

    if (!formData.category_id) {
      toast.error('La catégorie est requise')
      return
    }

    if (!formData.original_price || formData.original_price <= 0) {
      toast.error('Le prix original doit être supérieur à 0')
      return
    }

    if (!formData.discount_price || formData.discount_price <= 0) {
      toast.error('Le prix discount doit être supérieur à 0')
      return
    }

    if (formData.max_quantity !== null && formData.max_quantity !== undefined && formData.max_quantity <= 0) {
      toast.error('La quantité maximale doit être supérieure à 0')
      return
    }

    if (!selectedProducts.some(p => p.product_id.trim() !== '')) {
      toast.error('Au moins un produit doit être sélectionné')
      return
    }

    if (!formData.end_date) {
      toast.error('La date de fin est requise')
      return
    }

    if (formData.start_date && formData.end_date && new Date(formData.start_date) > new Date(formData.end_date)) {
      toast.error('La date de début doit être antérieure à la date de fin')
      return
    }

    // Convert datetime-local format to ISO string for API and parse string prices to numbers
    const prepareFormData = (data: CreateOfferDto): CreateOfferDto => {
      const convertToISO = (dateString: string | null | undefined) => {
        if (!dateString) return null
        // If already in ISO format (contains 'Z' or has timezone), keep as is
        if (dateString.includes('Z') || dateString.includes('+')) {
          return dateString
        }
        // Convert from YYYY-MM-DDTHH:mm to ISO string
        const date = new Date(dateString)
        return date.toISOString()
      }

      // Parse price fields from string to number if they are strings
      const parsePrice = (value: any): number | null => {
        if (value === null || value === undefined) return null
        if (typeof value === 'number') return value
        if (typeof value === 'string') {
          const trimmed = value.trim()
          if (trimmed === '') return null
          const num = parseFloat(trimmed)
          return isNaN(num) ? null : num
        }
        return null
      }

      return {
        ...data,
        original_price: parsePrice(data.original_price),
        discount_price: parsePrice(data.discount_price),
        discount_percentage: parsePrice(data.discount_percentage),
        start_date: convertToISO(data.start_date),
        end_date: convertToISO(data.end_date)
      }
    }

    try {
      // Create FormData for file upload
      const formDataToSend = new FormData();
      
      // Add all non-image fields
      const preparedData = prepareFormData(formData);
      Object.keys(preparedData).forEach(key => {
        const value = (preparedData as any)[key];
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            // For arrays like products, send as JSON
            formDataToSend.append(key, JSON.stringify(value));
          } else if (typeof value === 'boolean') {
            formDataToSend.append(key, value.toString());
          } else {
            formDataToSend.append(key, value.toString());
          }
        }
      });
      
      // Add delete_images for update
      if (id && imagesToDelete.length > 0) {
        formDataToSend.append('delete_images', JSON.stringify(imagesToDelete));
      }
      
      // Add uploaded files
      uploadedFiles.forEach(file => {
        formDataToSend.append('images', file);
      });
      
      // Use the store's createOffer/updateOffer methods which should handle FormData
      if (id) {
        await updateOffer(id, formDataToSend as any)
      } else {
        await createOffer(formDataToSend as any)
      }
      navigate('/offers')
    } catch (error) {
      console.error('Error saving offer:', error)
    }
  }

  const offerTypes = [
    { value: 'discount', label: 'Réduction' },
    { value: 'bundle', label: 'Pack' },
    { value: 'flash_sale', label: 'Vente flash' },
    { value: 'seasonal', label: 'Saisonnière' }
  ]

  // Compute currency symbol
  const currencySymbol = useMemo(() => {
    if (!systemConfig?.default_currency) return ''
    const currency = availableCurrencies.find(c => c.code === systemConfig.default_currency)
    return currency?.symbol || systemConfig.default_currency
  }, [systemConfig, availableCurrencies])

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate('/offers')}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Retour à la liste
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            {id ? 'Modifier l\'offre' : 'Nouvelle offre'}
          </h1>
          <p className="text-gray-600">
            {id ? 'Modifiez les informations de l\'offre' : 'Créez une nouvelle offre promotionnelle'}
          </p>
        </div>
      </div>

      {(error || formError) && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {formError || error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Colonne 1 - Informations de l'offre */}
          <DetailSection title="Informations de l'offre" className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">

            {/* Titre */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Titre de l'offre *
              </label>
              <div className="relative">
                <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  className="pl-10 w-full rounded-lg border border-gray-300 py-2 pr-3 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:border-primary-500 focus:ring-primary-500"
                  placeholder="Titre de l'offre"
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:border-primary-500 focus:ring-primary-500"
                placeholder="Description détaillée de l'offre"
              />
            </div>

            {/* Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type d'offre *
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                required
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:border-primary-500 focus:ring-primary-500"
              >
                {offerTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Marchand */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Marchand *
              </label>
              <div className="relative">
                <Store className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <select
                  name="merchant_id"
                  value={formData.merchant_id}
                  onChange={handleChange}
                  required
                  className="pl-10 w-full rounded-lg border border-gray-300 py-2 pr-3 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:border-primary-500 focus:ring-primary-500"
                >
                  <option value="">Sélectionner un marchand</option>
                  {merchants.map((merchant) => (
                    <option key={merchant.merchant_id} value={merchant.merchant_id}>
                      {merchant.business_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Catégorie */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Catégorie
              </label>
              <div className="relative">
                <Folder className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <select
                  name="category_id"
                  value={formData.category_id || ''}
                  onChange={handleChange}
                  className="pl-10 w-full rounded-lg border border-gray-300 py-2 pr-3 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:border-primary-500 focus:ring-primary-500"
                >
                  <option value="">Sélectionner une catégorie</option>
                  {categories.map((category) => (
                    <option key={category.category_id} value={category.category_id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Temps de préparation */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Temps de préparation (minutes)
              </label>
              <input
                type="number"
                name="preparation_time_minutes"
                value={formData.preparation_time_minutes ?? ''}
                onChange={handleChange}
                min={0}
                max={1440}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:border-primary-500 focus:ring-primary-500"
                placeholder="Ex: 15"
              />
              <p className="mt-1 text-xs text-gray-500">
                Durée estimée de préparation de la commande (optionnel)
              </p>
            </div>
          </DetailSection>

          {/* Colonne 2 - Tarification */}
          <DetailSection title="Tarification" className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">

            {/* Devise */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Devise
              </label>
              <select
                name="currency"
                value={formData.currency || ''}
                onChange={handleChange}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:border-primary-500 focus:ring-primary-500"
              >
                <option value="">Utiliser la devise par défaut ({systemConfig?.default_currency || ''})</option>
                {availableCurrencies.map((currency) => (
                  <option key={currency.code} value={currency.code}>
                    {currency.name} ({currency.code}) - {currency.symbol}
                  </option>
                ))}
              </select>
            </div>

            {/* Prix original */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prix original
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 font-medium">
                  {formData.currency
                    ? availableCurrencies.find(c => c.code === formData.currency)?.symbol || formData.currency
                    : currencySymbol}
                </span>
                <input
                  type="text"
                  inputMode="decimal"
                  name="original_price"
                  value={priceInputs.original_price}
                  onChange={handleChange}
                  onBlur={handlePriceBlur}
                  className="pl-10 w-full rounded-lg border border-gray-300 py-2 pr-3 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:border-primary-500 focus:ring-primary-500"
                  placeholder="0.00"
                />
              </div>
            </div>

            {/* Prix réduit */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prix réduit
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 font-medium">
                  {formData.currency
                    ? availableCurrencies.find(c => c.code === formData.currency)?.symbol || formData.currency
                    : currencySymbol}
                </span>
                <input
                  type="text"
                  inputMode="decimal"
                  name="discount_price"
                  value={priceInputs.discount_price}
                  onChange={handleChange}
                  onBlur={handlePriceBlur}
                  className="pl-10 w-full rounded-lg border border-gray-300 py-2 pr-3 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:border-primary-500 focus:ring-primary-500"
                  placeholder="0.00"
                />
              </div>
            </div>

            {/* Pourcentage de réduction */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pourcentage de réduction (%)
              </label>
              <div className="relative">
                <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  inputMode="decimal"
                  name="discount_percentage"
                  value={priceInputs.discount_percentage}
                  onChange={handleChange}
                  onBlur={handlePriceBlur}
                  className="pl-10 w-full rounded-lg border border-gray-300 py-2 pr-3 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:border-primary-500 focus:ring-primary-500"
                  placeholder="0.00"
                />
              </div>
            </div>

            {/* Quantité maximale */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quantité maximale
              </label>
              <input
                type="number"
                name="max_quantity"
                value={formData.max_quantity || ''}
                onChange={handleChange}
                min="0"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:border-primary-500 focus:ring-primary-500"
                placeholder="Illimité"
              />
            </div>
          </DetailSection>
        </div>

        {/* Période de validité */}
        <DetailSection title="Période de validité" className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Date de début */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date de début
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="datetime-local"
                  name="start_date"
                  value={formData.start_date || ''}
                  onChange={handleChange}
                  className="pl-10 w-full rounded-lg border border-gray-300 py-2 pr-3 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:border-primary-500 focus:ring-primary-500"
                />
              </div>
            </div>

            {/* Date de fin */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date de fin <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="datetime-local"
                  name="end_date"
                  value={formData.end_date || ''}
                  onChange={handleChange}
                  required
                  className="pl-10 w-full rounded-lg border border-gray-300 py-2 pr-3 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:border-primary-500 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>
        </DetailSection>

        {/* Surprise Bag */}
        <DetailSection title="Sac surprise" className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">

          {/* Surprise Bag Toggle */}
          <div className="flex items-center justify-between p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-amber-900">Sac surprise</p>
                <p className="text-xs text-amber-700">Activer le mode sac surprise pour cette offre</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="is_surprise_bag"
                checked={formData.is_surprise_bag || false}
                onChange={handleChange}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
            </label>
          </div>

          {/* Surprise Bag Fields (conditionally shown) */}
          {formData.is_surprise_bag && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                {/* Surprise Bag Price */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prix du sac surprise
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 font-medium">
                      {formData.currency
                        ? availableCurrencies.find(c => c.code === formData.currency)?.symbol || formData.currency
                        : currencySymbol}
                    </span>
                    <input
                      type="text"
                      inputMode="decimal"
                      name="surprise_bag_price"
                      value={priceInputs.surprise_bag_price}
                      onChange={handleChange}
                      onBlur={handlePriceBlur}
                      className="pl-10 w-full rounded-lg border border-gray-300 py-2 pr-3 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:border-primary-500 focus:ring-primary-500"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                {/* Surprise Bag Label */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Étiquette du sac surprise
                  </label>
                  <input
                    type="text"
                    name="surprise_bag_label"
                    value={formData.surprise_bag_label || ''}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:border-primary-500 focus:ring-primary-500"
                    placeholder="Ex: Surprise du chef, Panier mystère..."
                  />
                </div>
              </div>

              {/* Possible Contents */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contenu possible
                </label>
                <PossibleContentsEditor
                  contents={formData.possible_contents || []}
                  onChange={(newContents) => {
                    setFormData(prev => ({
                      ...prev,
                      possible_contents: newContents
                    }))
                  }}
                />
              </div>
            </>
          )}
        </DetailSection>

        {/* Section Images */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Images de l'offre</h2>
              <p className="text-sm text-gray-600">Ajoutez des images pour illustrer l'offre (max 10)</p>
            </div>
          </div>

          {/* File upload area */}
          <div className="mb-6">
            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <Upload className="h-8 w-8 text-gray-400 mb-2" />
                <p className="text-sm text-gray-500">
                  <span className="font-semibold">Cliquez pour télécharger</span> ou glissez-déposez
                </p>
                <p className="text-xs text-gray-400">PNG, JPG, GIF, WEBP jusqu'à 10MB</p>
              </div>
              <input
                type="file"
                className="hidden"
                accept="image/*"
                multiple
                onChange={handleFileUpload}
              />
            </label>
          </div>

          {/* Existing images */}
          {existingImages.length > 0 && (
            <div className="mb-6">
              <h4 className="text-md font-medium text-gray-700 mb-3">Images existantes</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {existingImages.map((img) => (
                  <div key={img.image_id} className="relative group">
                    <div className="aspect-square w-full rounded-lg overflow-hidden bg-gray-100">
                      {img.image_url ? (
                        <img
                          src={getImageUrl(img.image_url)}
                          alt={`Image de l'offre ${img.display_order + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const parent = e.currentTarget.parentElement;
                            if (parent) {
                              parent.innerHTML = `<div class="w-full h-full flex items-center justify-center text-gray-400"><svg class="h-12 w-12" ...></svg></div>`;
                            }
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <Package className="h-12 w-12" />
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeExistingImage(img.image_id)}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <XIcon className="h-4 w-4" />
                    </button>
                    {img.is_primary && (
                      <span className="absolute top-2 left-2 bg-primary-600 text-white text-xs px-2 py-1 rounded">
                        Principale
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* New image previews */}
          {imagePreviews.length > 0 && (
            <div className="mb-6">
              <h4 className="text-md font-medium text-gray-700 mb-3">Nouvelles images</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {imagePreviews.map((preview, index) => (
                  <div key={index} className="relative group">
                    <div className="aspect-square w-full rounded-lg overflow-hidden bg-gray-100">
                      <img
                        src={preview}
                        alt={`Nouvelle image ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeUploadedFile(index)}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <XIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Section Produits */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Produits inclus</h2>
              <p className="text-sm text-gray-600">Ajoutez au moins un produit à l'offre</p>
            </div>
            <button
              type="button"
              onClick={handleAddProduct}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <Plus className="h-5 w-5 mr-2" />
              Ajouter un produit
            </button>
          </div>

          {selectedProducts.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
              <Package className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">Aucun produit ajouté</p>
              <p className="text-sm text-gray-400 mt-1">Cliquez sur "Ajouter un produit" pour commencer</p>
            </div>
          ) : (
            <div className="space-y-4">
              {selectedProducts.map((product, index) => (
                <div key={index} className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Produit
                    </label>
                    <select
                      value={product.product_id}
                      onChange={(e) => handleProductChange(index, 'product_id', e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:border-primary-500 focus:ring-primary-500"
                    >
                      <option value="">Sélectionner un produit</option>
                      {products.map((p) => (
                        <option key={p.product_id} value={p.product_id}>
                          {p.name} - {p.price} {systemConfig?.default_currency || ''} (Stock: {p.stock_quantity})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="w-32">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Quantité
                    </label>
                    <input
                      type="number"
                      value={product.quantity}
                      onChange={(e) => handleProductChange(index, 'quantity', e.target.value)}
                      min="1"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:border-primary-500 focus:ring-primary-500"
                    />
                  </div>
                  <div className="pt-6">
                    <button
                      type="button"
                      onClick={() => handleRemoveProduct(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/offers')}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                Enregistrement...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 inline mr-2" />
                {id ? 'Mettre à jour' : 'Créer l\'offre'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

// ─── PossibleContentsEditor ─────────────────────────────────────────────────
interface PossibleContentsEditorProps {
  contents: string[]
  onChange: (contents: string[]) => void
}

function PossibleContentsEditor({ contents, onChange }: PossibleContentsEditorProps) {
  const [inputValue, setInputValue] = useState('')

  const handleAdd = () => {
    const trimmed = inputValue.trim()
    if (!trimmed) return
    if (contents.includes(trimmed)) {
      toast.error('Ce contenu est déjà dans la liste')
      return
    }
    onChange([...contents, trimmed])
    setInputValue('')
  }

  const handleRemove = (index: number) => {
    onChange(contents.filter((_, i) => i !== index))
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAdd()
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className="flex-1 rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:border-primary-500 focus:ring-primary-500"
          placeholder="Saisissez un élément du contenu possible..."
        />
        <button
          type="button"
          onClick={handleAdd}
          disabled={!inputValue.trim()}
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="h-4 w-4 mr-1" />
          Ajouter
        </button>
      </div>
      {contents.length === 0 ? (
        <p className="text-sm text-gray-400 italic">
          Aucun contenu défini. Ajoutez les éléments possibles du sac surprise.
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {contents.map((item, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-50 text-amber-800 rounded-full text-sm border border-amber-200"
            >
              {item}
              <button
                type="button"
                onClick={() => handleRemove(index)}
                className="ml-1 text-amber-500 hover:text-amber-700 focus:outline-none"
                aria-label={`Supprimer ${item}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

export default OfferForm
